import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { browseCollections } from '@platform/client';
import { NO_PATH, collectionSortSchema } from '@platform/schema/authoring';
import { contentStatusSchema, subjectSchema } from '@platform/schema/content';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { RowPublishCell } from '../authoring/PublishDialog';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import { CollectionPublishDialog } from './CollectionPublishDialog';
import { counted } from './counted';
import { FilterSelect, sameAs } from './FilterSelect';
import { ModulePicker } from './ModulePicker';
import { PAGE, Pager } from './Pager';
import { jobsQuery, pickerQuery } from './sources';
import { STATUS_COLOUR, archived } from './status';
import { SUBJECT_OPTIONS, subjectLabel } from './subject';

import type { Collection } from './collection';
import type { Publishing } from './CollectionPublishDialog';
import type { CollectionSort } from '@platform/schema/authoring';
import type { CollectionKind } from '@platform/schema/content';

/**
 * A filter only some kinds have. `course`, `path` and `job` narrow modules; `module`
 * narrows paths — PATH-02.
 */
type Narrow = 'course' | 'path' | 'job' | 'module';

/**
 * The screen the console did not have — GEN-58.
 *
 * `apps/admin` had five routes and none of them listed content: a course was reachable
 * only if you already knew its id, and the only way to get one was from a job you
 * remembered running. The console could make content and could not browse it.
 *
 * **This is the pattern the other three follow** — GEN-59, GEN-60 and GEN-61 are the same
 * screen over `PATH`, `MODULE` and lessons. It is one component taking a `kind` rather
 * than four, because four screens invented separately are four different screens, and the
 * differences would all be accidental.
 */

export function Library({
  kind,
  title,
  detailPath,
  newPath,
  narrows = [],
  publishes = false,
}: {
  kind: CollectionKind;
  title: string;
  /** Where a row goes. Each kind owns its own detail screen. */
  detailPath: (id: string) => string;
  /**
   * Where New goes, or nothing where this kind has no way to make one yet. A button
   * pointing at a route that does not exist is worse than no button — it looks like the
   * feature is there and lands on NotFound. Courses and paths have one; modules and
   * lessons still do not.
   */
  newPath?: string;
  /**
   * Filters this kind has and the others do not — GEN-60, and the parameter
   * `library-screens.md` asks for in place of a second component.
   *
   * Both are about a MODULE and neither means anything for the other two: a course has no
   * course to belong to, and a run produces modules. Held here rather than passed in as
   * controls, so the value a control sets and the value the query sends cannot drift
   * apart — a filter that renders and never reaches the server is the failure this
   * screen's whole design is arguing against.
   */
  narrows?: readonly Narrow[];
  /** An Actions column with each row's publish — ADMIN-29. Courses and modules publish. */
  publishes?: boolean;
}) {
  const [q, setQ] = useState('');
  const [subject, setSubject] = useState('');
  const [status, setStatus] = useState('');
  const [course, setCourse] = useState('');
  const [job, setJob] = useState('');
  const [offset, setOffset] = useState(0);
  const [publishing, setPublishing] = useState<Publishing | null>(null);
  // In the URL, so a refresh keeps them — PATH-02. Both are slugs.
  const [params, setParams] = useSearchParams();
  const path = params.get('path') ?? '';
  const module = params.get('module') ?? '';
  const sort = collectionSortSchema
    .catch('UPDATED_DESC')
    .parse(params.get('sort'));
  const arrow = sortedBy(sort);

  const byCourse = narrows.includes('course');
  const byPath = narrows.includes('path');
  const byJob = narrows.includes('job');
  const byModule = narrows.includes('module');

  // Which course a module belongs to — GEN-72. Read off the kind rather than off the
  // Course filter: the two coincide today, and a `collection_nesting_is_one_level` CHECK
  // is what makes this one true, where the filter set is a screen's choice.
  const underCourse = kind === 'MODULE';

  const courses = useQuery({ ...pickerQuery('COURSE'), enabled: byCourse });
  const paths = useQuery({ ...pickerQuery('PATH'), enabled: byPath });
  const jobs = useQuery({ ...jobsQuery(), enabled: byJob });

  const page = useQuery({
    queryKey: [
      'library',
      kind,
      q,
      subject,
      status,
      course,
      path,
      job,
      module,
      sort,
      offset,
    ],
    queryFn: () =>
      browseCollections({
        query: {
          kind,
          limit: PAGE,
          offset,
          ...(q === '' ? {} : { q }),
          ...(subject === '' ? {} : { subject: subjectSchema.parse(subject) }),
          ...(status === ''
            ? {}
            : { status: contentStatusSchema.parse(status) }),
          ...(course === '' ? {} : { parentId: course }),
          ...(job === '' ? {} : { jobId: job }),
          ...(path === '' ? {} : { path }),
          ...(module === '' ? {} : { module }),
          sort,
        },
        throwOnError: true,
      }),
  });

  const rows = page.data?.data.collections ?? [];
  const total = page.data?.data.total ?? 0;
  const filtered = [q, subject, status, course, path, job, module].some(
    (value) => value !== '',
  );

  // Every filter change starts a new list, so a page offset from the old one would land
  // somewhere arbitrary in the new one.
  const refine = (set: (value: string) => void, value: string): void => {
    set(value);
    setOffset(0);
  };
  const refineUrl = (key: 'path' | 'module' | 'sort', value: string): void => {
    refine((next) => {
      setParams(
        (was) => {
          const now = new URLSearchParams(was);
          if (next === '') now.delete(key);
          else now.set(key, next);
          return now;
        },
        { replace: true },
      );
    }, value);
  };

  return (
    <Stack spacing={SCREEN_GAP}>
      <PageHeader
        title={title}
        action={
          newPath === undefined ? undefined : (
            <Button variant="contained" component={Link} to={newPath}>
              New
            </Button>
          )
        }
      />

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <TextField
          size="small"
          label="Search title or slug"
          value={q}
          onChange={(event) => {
            refine(setQ, event.target.value);
          }}
          sx={{ minWidth: 260 }}
        />
        <FilterSelect
          label="Topic"
          value={subject}
          width={180}
          options={SUBJECT_OPTIONS}
          onChange={(value) => {
            refine(setSubject, value);
          }}
        />
        <FilterSelect
          label="Status"
          value={status}
          width={160}
          options={sameAs(contentStatusSchema.options)}
          onChange={(value) => {
            refine(setStatus, value);
          }}
        />
        {byCourse && (
          <FilterSelect
            label="Course"
            value={course}
            width={220}
            options={(courses.data?.data.collections ?? []).map((one) => ({
              value: one.id,
              label: one.title,
            }))}
            onChange={(value) => {
              refine(setCourse, value);
            }}
          />
        )}
        {byPath && (
          <FilterSelect
            label="Path"
            value={path}
            width={220}
            options={[
              { value: NO_PATH, label: 'No path' },
              ...(paths.data?.data.collections ?? []).map((one) => ({
                value: one.slug,
                label: `${one.title}${archived(one.status)}`,
              })),
            ]}
            onChange={(value) => {
              refineUrl('path', value);
            }}
          />
        )}
        {byModule && (
          <ModulePicker
            width={260}
            {...(module !== '' && { initialSlug: module })}
            onChange={(picked) => {
              refineUrl('module', picked?.slug ?? '');
            }}
          />
        )}
        {byJob && (
          <FilterSelect
            label="Job"
            value={job}
            width={220}
            options={(jobs.data?.data.jobs ?? []).map((one) => ({
              value: one.id,
              // The last eight, as the jobs list labels them, plus the day — an id alone
              // is not something anyone recognises their own run by.
              label: `${one.id.slice(-8)} · ${new Date(one.createdAt).toLocaleDateString()}`,
            }))}
            onChange={(value) => {
              refine(setJob, value);
            }}
          />
        )}
      </Stack>

      {page.isPending && <LinearProgress />}

      {/* An empty state is an invitation, not a report — and the two emptinesses need
          different sentences. Nothing here yet is a prompt to make one; nothing matching
          is a prompt to widen the search. */}
      {!page.isPending && rows.length === 0 && (
        <Alert severity="info">
          {filtered
            ? 'Nothing matches those filters. Clear one to see more.'
            : `No ${title.toLowerCase()} yet.${newPath === undefined ? '' : ' Use New to make the first one, or run a generation.'}`}
        </Alert>
      )}

      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              {underCourse && <TableCell>Course</TableCell>}
              {underCourse && (
                <TableCell sortDirection={arrow}>
                  <TableSortLabel
                    active={arrow !== false}
                    direction={arrow === false ? 'asc' : arrow}
                    onClick={() => {
                      refineUrl(
                        'sort',
                        sort === 'PATHS_ASC' ? 'PATHS_DESC' : 'PATHS_ASC',
                      );
                    }}
                  >
                    Paths
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell>Topic</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Holds</TableCell>
              <TableCell>Updated</TableCell>
              {publishes && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Link to={detailPath(row.id)}>{row.title}</Link>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    noWrap
                  >
                    {row.slug}
                  </Typography>
                </TableCell>
                {underCourse && <TableCell>{row.parentTitle ?? '—'}</TableCell>}
                {underCourse && (
                  <TableCell>
                    <PathsHeld paths={row.paths} />
                  </TableCell>
                )}
                <TableCell>
                  {row.subject === undefined ? '—' : subjectLabel(row.subject)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLOUR[row.status]}
                    label={row.status}
                  />
                </TableCell>
                <TableCell align="right">
                  {row.kind === 'COURSE'
                    ? counted(row.modules, 'module')
                    : row.kind === 'PATH'
                      ? `${counted(row.modules, 'module')}, ${counted(row.lessons, 'lesson')}`
                      : counted(row.lessons, 'lesson')}
                </TableCell>
                <TableCell>
                  {new Date(row.updatedAt).toLocaleDateString()}
                </TableCell>
                {publishes && (
                  <RowPublishCell
                    status={row.status}
                    title={row.title}
                    onClick={() => {
                      setPublishing(row);
                    }}
                  />
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CollectionPublishDialog
        target={publishing}
        onClose={() => {
          setPublishing(null);
        }}
      />

      <Pager offset={offset} total={total} onOffset={setOffset} />
    </Stack>
  );
}

/** The header's arrow, or none while the list is in its default order. */
const sortedBy = (sort: CollectionSort): 'asc' | 'desc' | false =>
  sort === 'PATHS_ASC' ? 'asc' : sort === 'PATHS_DESC' ? 'desc' : false;

/** Up to two of a module's paths as links, then "+N"; every one on hover — PATH-02. */
const SHOWN_PATHS = 2;

function PathsHeld({ paths }: { paths: Collection['paths'] }) {
  if (paths.length === 0)
    return (
      <Typography variant="body2" color="text.secondary">
        No path
      </Typography>
    );

  return (
    <Tooltip
      title={paths
        .map((one) => `${one.title}${archived(one.status)}`)
        .join(', ')}
    >
      <span>
        {paths.slice(0, SHOWN_PATHS).map((one, index) => (
          <Fragment key={one.id}>
            {index > 0 && ', '}
            <Link to={`/library/paths/${one.id}`}>{one.title}</Link>
            {archived(one.status)}
          </Fragment>
        ))}
        {paths.length > SHOWN_PATHS &&
          ` +${String(paths.length - SHOWN_PATHS)}`}
      </span>
    </Tooltip>
  );
}
